import React, { useEffect, useMemo, useState } from 'react';
import { OpenSeaDragon } from '../OpenSeaDragon';
import { Button } from '../Button';
import { IconArrowLeft, IconArrowRight } from '../Icons';
import { LotShowcaseThumbnails } from './LotShowcaseThumbnails';
import { cn } from '../cn';

type TLotShowcaseProps = {
  CDN_PATH: string;
  auctionId: number;
  videos: string[];
  photos: (string | string[])[];
  osdWrapperClassName?: string;
}

export const LotShowcase = ({CDN_PATH, auctionId, videos, photos, ...props}: TLotShowcaseProps) => {
  const [activeVideoIndex, setActiveVideoIndex] = useState(-1);
  // combine photo index states because they can depend on each other's state when updating
  const [activeIndices, setActiveIndices] = useState({ photo: 0, subPhoto: 0 });
  // destructure individual values and setters
  const activePhotoIndex = activeIndices.photo;
  const activeSubPhotoIndex = activeIndices.subPhoto;
  const setActivePhotoIndex = (value: number): void => setActiveIndices((prev) => ({ ...prev, photo: value }));
  const setActiveSubPhotoIndex = (value: number): void => setActiveIndices((prev) => ({ ...prev, subPhoto: value }));

  useEffect(() => {
    setActiveIndices({photo: 0, subPhoto: 0});
    setActiveVideoIndex(-1);
  }, [auctionId, photos, videos]) //on lot change, basically
  const lotShowcaseThumbnails = useMemo(() => {
    return photos?.map((x) => {
      return `${CDN_PATH}${auctionId}/thumbs/${x}`;
    });
  }, [photos, auctionId, CDN_PATH]);

  function goToNextPhoto(): void {
    // is current photo part of a subgroup and not the last one
    // in that group? subphotoindex is 1-based when group exists
    if (activeSubPhotoIndex && photos[activePhotoIndex][activeSubPhotoIndex]) {
      setActiveSubPhotoIndex(activeSubPhotoIndex + 1);
      return;
    }
    // check next photo index exists
    if (photos[activePhotoIndex + 1]) {
      // single photo string or sub photo array determines subindex
      const newSub = Array.isArray(photos[activePhotoIndex + 1])
        ? 1 : 0;
      // move to next photo index
      setActiveIndices((prev) => ({ photo: prev.photo + 1, subPhoto: newSub }));
    }
  }

  function goToPrevPhoto(): void {
    // is current photo part of a subgroup and not the first one
    // in that group? subphotoindex is 1-based when group exists
    if (activeSubPhotoIndex && activeSubPhotoIndex !== 1 && photos[activePhotoIndex][activeSubPhotoIndex - 1]) {
      setActiveSubPhotoIndex(activeSubPhotoIndex - 1);
      return;
    }
    // check previous photo index exists
    if (photos[activePhotoIndex - 1]) {
      // single photo string or sub photo array determines subindex
      const newSub = Array.isArray(photos[activePhotoIndex - 1]) ? photos[activePhotoIndex - 1].length : 0;
      // move to previous photo index
      setActiveIndices((prev) => ({ photo: prev.photo - 1, subPhoto: newSub }));
    }
  }

  let hasNextPhoto = false;
  try {
    if (photos?.length > 0) {
      hasNextPhoto = !!(
        (activeSubPhotoIndex && photos[activePhotoIndex][activeSubPhotoIndex])
        || photos[activePhotoIndex + 1]
      );
    }
  } catch (e) {
    console.error('Error in checking hasNextPhoto', e);
  }
  let hasPrevPhoto = false;
  try {
    if (photos?.length > 0) {
      hasPrevPhoto = !!(
        (
          activeSubPhotoIndex
          && activeSubPhotoIndex !== 1
          && photos[activePhotoIndex][activeSubPhotoIndex - 1]
        )
        || photos[activePhotoIndex - 1]
      );
    }
  } catch (e) {
    console.error('Error in checking hasPrevPhoto', e);
  }

  return (<>
    <div className='flex flex-1 flex-col gap-3  xl:h-full'>
      <div className='flex-1'>
        <div className={cn('flex flex-1 detail-page-osd-wrapper gap-5 w-full h-[356px] xl:h-[400px]', props.osdWrapperClassName)}>
          <style dangerouslySetInnerHTML={{
            __html: `
            .detail-page-osd-wrapper #osd,.detail-page-iframe-video {
              margin: auto;
              box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
              overflow: hidden;
              max-height: 100%;
            }
            .openseadragon-canvas-container__fullscreen #osd {
              aspect-ratio: unset;  
              border-radius: 0;
            }
          ` }}>
          </style>
          {activeVideoIndex !== -1
            &&
              <div className='w-full h-full flex items-center justify-center overflow-hidden p-1'>
                <iframe
                  title='YouTube Video'
                  className='block aspect-video h-full max-h-full w-auto max-w-full'
                  src={videos[activeVideoIndex] + '?rel=0&enablejsapi=1'}
                />
              </div>
            || <OpenSeaDragon
              photo={
                `${CDN_PATH}${auctionId}/${activeSubPhotoIndex > 0 ? (
                  photos[activePhotoIndex][activeSubPhotoIndex - 1]
                ) : (
                  photos[activePhotoIndex]
                )}`
              }
            >
              {hasNextPhoto && <Button className='absolute right-1 top-1/2 z-10 px-1 opacity-50 hover:opacity-100' onPress={() => goToNextPhoto()}>
                <IconArrowRight/>
              </Button>}
              {hasPrevPhoto && <Button className='absolute left-1 top-1/2 z-10 px-1 opacity-50 hover:opacity-100' onPress={() => goToPrevPhoto()}>
                <IconArrowLeft/>
              </Button>}
            </OpenSeaDragon>
          }
        </div>
      </div>
      <div className='flex-col gap-4 h-[180px] flex xl:hidden'>
        <LotShowcaseThumbnails
          thumbs={lotShowcaseThumbnails}
          activeImage={activePhotoIndex}
          setActiveImage={setActivePhotoIndex}
          loading={false}
          direction='horizontal'
          setActiveVideoIndex={setActiveVideoIndex}
          activeVideoIndex={activeVideoIndex}
          videos={videos}
        />
      </div>
    </div>
    <div className='flex-1 flex-col gap-4 max-w-[167px] hidden xl:flex'>
      <LotShowcaseThumbnails
        thumbs={lotShowcaseThumbnails}
        activeImage={activePhotoIndex}
        setActiveImage={setActivePhotoIndex}
        loading={false}
        setActiveVideoIndex={setActiveVideoIndex}
        activeVideoIndex={activeVideoIndex}
        videos={videos}
      />
    </div>
  </>)
}